import { useState, useEffect } from "react"
import { Box, Flex, Text, Badge } from "@chakra-ui/react"
import axios from "axios"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import ArticleStatus from "../../ArticleStatus"
import { neumorph } from "../neumorph"
import { formatCurrency, formatNum } from "../KpiHeader"

const STATUS_LABELS = {
  "04": "Active",
  "05": "New",
  "07": "Pending Delist",
  "60": "Blocked",
  "61": "Delisted (Store)",
  "62": "Delisted (DC)",
  "71": "Phase Out",
  "72": "Discontinued",
  "73": "Returns",
  "7C": "Clearance",
  "7E": "Emergency",
  unknown: "No Status",
}

const STATUS_COLORS = {
  "04": "green",
  "05": "blue",
  "07": "yellow",
  "60": "red",
  "61": "orange",
  "62": "orange",
  "71": "red",
  "72": "red",
  "73": "red",
  "7C": "purple",
  "7E": "red",
  unknown: "gray",
}

const ArticleStatusCard = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const isPnp = retailer?.toUpperCase() === "PNP"

  useEffect(() => {
    if (!supplier || !retailer || !isPnp) {
      setSummary([])
      return
    }
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/insights/article-status`,
          { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        )
        if (res.data?.status === 1) setSummary(res.data.data?.summary || [])
        else setSummary([])
      } catch (err) {
        if (!axios.isCancel(err)) console.error("ArticleStatusCard fetch error:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer, isPnp])

  if (!isPnp) {
    return (
      <BentoCard title="📊 Article Status" subtitle="PNP only">
        <Flex h="100%" align="center" justify="center" direction="column" textAlign="center">
          <Text fontSize="xs" color={neumorph.textMuted}>
            Status codes are only populated by PNP EDI.
          </Text>
          <Text fontSize="2xs" color={neumorph.textMuted} mt={1}>
            {retailer?.toUpperCase()} doesn't supply this data.
          </Text>
        </Flex>
      </BentoCard>
    )
  }

  const active = summary.find((r) => r.status_code === "04")
  const nonActive = summary.filter((r) => r.status_code !== "04")
  const totalNonActiveValue = nonActive.reduce((s, r) => s + (parseFloat(r.total_soh_value) || 0), 0)

  return (
    <>
      <BentoCard
        title="📊 Article Status (PNP)"
        subtitle="Active vs discontinued in stores"
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        {summary.length === 0 ? (
          <Text fontSize="xs" color={neumorph.textMuted}>
            No data
          </Text>
        ) : (
          <Flex direction="column" h="100%" gap={2}>
            <Flex gap={3}>
              <Box>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  Active SOH
                </Text>
                <Text fontSize="md" fontWeight="700" color={neumorph.accentGreen}>
                  {formatCurrency(active?.total_soh_value)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  Non-Active SOH
                </Text>
                <Text fontSize="md" fontWeight="700" color={neumorph.accentOrange}>
                  {formatCurrency(totalNonActiveValue)}
                </Text>
              </Box>
            </Flex>
            <Box mt={1}>
              <Text fontSize="2xs" color={neumorph.textMuted} mb={1} fontWeight="600" textTransform="uppercase">
                Status breakdown
              </Text>
              {summary.slice(0, 5).map((s) => (
                <Flex
                  key={s.status_code}
                  py={1}
                  align="center"
                  justify="space-between"
                  gap={2}
                >
                  <Flex align="center" gap={2}>
                    <Badge colorScheme={STATUS_COLORS[s.status_code] || "gray"} fontSize="2xs">
                      {s.status_code}
                    </Badge>
                    <Text fontSize="xs" color={neumorph.textPrimary}>
                      {STATUS_LABELS[s.status_code] || s.status_code}
                    </Text>
                  </Flex>
                  <Text fontSize="xs" fontWeight="600" color={neumorph.textPrimary}>
                    {formatNum(s.article_count)} art · {formatCurrency(s.total_soh_value)}
                  </Text>
                </Flex>
              ))}
            </Box>
          </Flex>
        )}
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Article Status Analysis">
        <ArticleStatus supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default ArticleStatusCard
